import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useSubscription } from '@/hooks/useSubscription';
import ApiTokenService, {
  type ApiToken,
  type CreateApiTokenRequest,
} from '@/services/ApiTokenService';
import { OAuthClientService, type OAuthClient, type CreateOAuthClientRequest } from '@/services/OAuthClientService';
import BillingService, {
  type SubscriptionPlan,
} from '@/services/BillingService';
import { SignOutButton, useAuth } from '@clerk/clerk-react';
import { useUser } from '@clerk/clerk-react';
import {
  Bell,
  Bot,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Crown,
  Key,
  LogOut,
  Mail,
  Phone,
  Plus,
  Shield,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

export default function SettingsPage() {
  const { user, isLoaded } = useUser();
  const subscription = useSubscription();
  const queryClient = useQueryClient();
  const [showPlans, setShowPlans] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<
    'monthly' | 'annual'
  >('monthly');
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [tokenForm, setTokenForm] = useState<CreateApiTokenRequest>({
    name: '',
    description: '',
    expiresInDays: null,
  });
  const [showMcpGuide, setShowMcpGuide] = useState(false);
  const [selectedTokenForConfig, setSelectedTokenForConfig] = useState<string | null>(null);
  const [showOAuthDialog, setShowOAuthDialog] = useState(false);
  const [newOAuthClient, setNewOAuthClient] = useState<{ clientId: string; clientSecret: string } | null>(null);
  const [oauthForm, setOauthForm] = useState<CreateOAuthClientRequest>({
    name: '',
    description: '',
    expiresInDays: null,
  });

  // API Tokens queries
  const {
    data: tokens,
    isLoading: tokensLoading,
    refetch: refetchTokens,
  } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => ApiTokenService.listTokens(),
  });

  const createTokenMutation = useMutation({
    mutationFn: (data: CreateApiTokenRequest) =>
      ApiTokenService.createToken(data),
    onSuccess: (data) => {
      setNewToken(data.token);
      setTokenForm({ name: '', description: '', expiresInDays: null });
      refetchTokens();
      toast.success('API token created successfully!');
    },
    onError: () => {
      toast.error('Failed to create API token');
    },
  });

  const revokeTokenMutation = useMutation({
    mutationFn: (id: number) => ApiTokenService.revokeToken(id),
    onSuccess: () => {
      refetchTokens();
      toast.success('Token revoked successfully');
    },
    onError: () => {
      toast.error('Failed to revoke token');
    },
  });

  const deleteTokenMutation = useMutation({
    mutationFn: (id: number) => ApiTokenService.deleteToken(id),
    onSuccess: () => {
      refetchTokens();
      toast.success('Token deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete token');
    },
  });

  const handleCreateToken = () => {
    if (!tokenForm.name.trim()) {
      toast.error('Token name is required');
      return;
    }
    createTokenMutation.mutate(tokenForm);
  };

  // OAuth Clients queries
  const {
    data: oauthClients,
    isLoading: oauthClientsLoading,
    refetch: refetchOAuthClients,
  } = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: () => OAuthClientService.list(),
  });

  const createOAuthClientMutation = useMutation({
    mutationFn: (data: CreateOAuthClientRequest) =>
      OAuthClientService.create(data),
    onSuccess: (data) => {
      setNewOAuthClient({
        clientId: data.clientId,
        clientSecret: data.clientSecret,
      });
      setOauthForm({ name: '', description: '', expiresInDays: null });
      refetchOAuthClients();
      toast.success('OAuth client created successfully!');
    },
    onError: () => {
      toast.error('Failed to create OAuth client');
    },
  });

  const revokeOAuthClientMutation = useMutation({
    mutationFn: (id: number) => OAuthClientService.revoke(id),
    onSuccess: () => {
      refetchOAuthClients();
      toast.success('OAuth client revoked successfully');
    },
    onError: () => {
      toast.error('Failed to revoke OAuth client');
    },
  });

  const deleteOAuthClientMutation = useMutation({
    mutationFn: (id: number) => OAuthClientService.delete(id),
    onSuccess: () => {
      refetchOAuthClients();
      toast.success('OAuth client deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete OAuth client');
    },
  });

  const handleCreateOAuthClient = () => {
    if (!oauthForm.name.trim()) {
      toast.error('OAuth client name is required');
      return;
    }
    createOAuthClientMutation.mutate(oauthForm);
  };

  const handleRevokeOAuthClient = (id: number) => {
    if (confirm('Are you sure you want to revoke this OAuth client?')) {
      revokeOAuthClientMutation.mutate(id);
    }
  };

  const handleDeleteOAuthClient = (id: number) => {
    if (confirm('Are you sure you want to delete this OAuth client? This action cannot be undone.')) {
      deleteOAuthClientMutation.mutate(id);
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    toast.success('Token copied to clipboard!');
  };

  const handleRevokeToken = (id: number) => {
    if (confirm('Are you sure you want to revoke this token?')) {
      revokeTokenMutation.mutate(id);
    }
  };

  const handleDeleteToken = (id: number) => {
    if (
      confirm(
        'Are you sure you want to permanently delete this token? This cannot be undone.',
      )
    ) {
      deleteTokenMutation.mutate(id);
    }
  };

  // Check if we should show plans from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (urlParams.get('showPlans') === 'true') {
      setShowPlans(true);
      loadPlans();
    }

    // Check for success message - invalidate subscription cache to get fresh data
    if (urlParams.get('success') === 'true') {
      const plan = urlParams.get('plan');
      const confirmAndRefresh = async () => {
        try {
          if (sessionId) {
            await BillingService.confirmCheckout(sessionId);
          }
          toast.success(`Successfully subscribed to ${plan} plan!`);
        } catch (_e) {
          // Even if confirm fails, still trigger a refetch
        } finally {
          sessionStorage.setItem('subscription_updated', Date.now().toString());
          await queryClient.invalidateQueries({ queryKey: ['subscription'] });
          await queryClient.refetchQueries({ queryKey: ['subscription'] });
          // Clean URL
          window.history.replaceState({}, '', '/app/settings');
        }
      };
      void confirmAndRefresh();
    }
  }, [queryClient]);

  // Refetch subscription data when window regains focus or becomes visible
  // (useful after returning from Stripe checkout in a new tab/window)
  useEffect(() => {
    const handleFocus = () => {
      const urlParams = new URLSearchParams(window.location.search);
      // If we have checkout params, invalidate and refetch subscription
      if (urlParams.get('plan') || urlParams.get('success')) {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.refetchQueries({ queryKey: ['subscription'] });
      }
    };

    const handleVisibilityChange = () => {
      // When page becomes visible, check if we need to refresh subscription
      if (document.visibilityState === 'visible') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('plan') || urlParams.get('success')) {
          queryClient.invalidateQueries({ queryKey: ['subscription'] });
          queryClient.refetchQueries({ queryKey: ['subscription'] });
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const result = await BillingService.getSubscriptionPlans();
      if (result.success) {
        setPlans(result.plans);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (
    planId: string,
    interval: 'monthly' | 'annual',
  ) => {
    try {
      setLoading(true);
      const result = await BillingService.createCheckoutSession(
        planId as any,
        interval,
      );
      if (result.success && result.url) {
        window.location.href = result.url;
      } else {
        toast.error('Failed to start checkout process');
      }
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await BillingService.createCustomerPortalSession();
      if (result.success && result.url) {
        if (result.needsPlanSelection) {
          setShowPlans(true);
          loadPlans();
        } else if (result.isCheckout) {
          toast.success('Redirecting to plan selection...');
          window.location.href = result.url;
        } else {
          toast.success('Opening subscription management...');
          window.location.href = result.url;
        }
      } else {
        toast.error('Failed to open subscription management');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open subscription management');
    }
  };

  // Show loading state while user data is loading
  if (!isLoaded) {
    return (
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Show plan selection if needed
  if (showPlans) {
    return (
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => setShowPlans(false)}
            className="mb-4"
          >
            ← Back to Settings
          </Button>
          <h1 className="text-3xl font-bold">Choose Your Plan</h1>
          <p className="text-muted-foreground mt-2">
            Select the plan that best fits your needs
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            {/* Billing Interval Tabs */}
            <div className="flex justify-center mb-8">
              <div className="bg-muted p-1 rounded-lg">
                <Button
                  variant={selectedInterval === 'monthly' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedInterval('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={selectedInterval === 'annual' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedInterval('annual')}
                >
                  Annual
                  <Badge variant="secondary" className="ml-2 text-xs">
                    Save 20%
                  </Badge>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const currentPricing =
                  selectedInterval === 'monthly' ? plan.monthly : plan.annual;
                const isAnnual = selectedInterval === 'annual';
                const monthlyEquivalent = isAnnual
                  ? (plan.annual.amount / 12 / 100).toFixed(2)
                  : null;

                return (
                  <Card key={plan.id} className="relative">
                    {plan.id === 'pro' && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-center">{plan.name}</CardTitle>
                      <CardDescription className="text-center">
                        {plan.id === 'starter' && 'Perfect for getting started'}
                        {plan.id === 'pro' &&
                          'Professional plan with advanced features'}
                        {plan.id === 'premium' &&
                          'Premium plan with all features'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold">
                          ${(currentPricing.amount / 100).toFixed(2)}
                        </div>
                        <div className="text-muted-foreground">
                          per {isAnnual ? 'year' : 'month'}
                          {isAnnual && monthlyEquivalent && (
                            <div className="text-sm text-green-600">
                              ${monthlyEquivalent}/month
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          <span className="text-sm">
                            {plan.id === 'starter' && 'Up to 5 assets'}
                            {plan.id === 'pro' && 'Up to 50 assets'}
                            {plan.id === 'premium' && 'Unlimited assets'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          <span className="text-sm">
                            {plan.id === 'starter' && '1GB storage'}
                            {plan.id === 'pro' && '10GB storage'}
                            {plan.id === 'premium' && '100GB storage'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          <span className="text-sm">
                            {plan.id === 'starter' && 'Basic analytics'}
                            {plan.id === 'pro' && 'Advanced analytics'}
                            {plan.id === 'premium' &&
                              'Premium analytics + AI insights'}
                          </span>
                        </div>
                        {plan.id !== 'starter' && (
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="text-sm">Priority support</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Button
                          className="w-full"
                          onClick={() =>
                            handleSelectPlan(plan.id, selectedInterval)
                          }
                          disabled={loading}
                        >
                          {loading
                            ? 'Loading...'
                            : `Choose ${plan.name} (${selectedInterval === 'monthly' ? 'Monthly' : 'Annual'})`}
                        </Button>
                        {isAnnual && (
                          <div className="text-center text-sm text-green-600 font-medium">
                            Save $
                            {(
                              (plan.monthly.amount * 12 - plan.annual.amount) /
                              100
                            ).toFixed(2)}{' '}
                            per year
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl">
      {/* User Info Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Account Overview
          </CardTitle>
          <CardDescription>
            Your current account information and status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {user?.fullName ||
                        user?.firstName ||
                        user?.lastName ||
                        'Not set'}
                    </p>
                    <p className="text-sm text-muted-foreground">Full Name</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {user?.primaryEmailAddress?.emailAddress ||
                        user?.emailAddresses?.[0]?.emailAddress ||
                        'No email'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Primary Email
                    </p>
                  </div>
                </div>

                {user?.phoneNumbers?.[0] && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Phone className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {user.phoneNumbers[0].phoneNumber}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Phone Number
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {user?.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : 'Unknown'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Member Since
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {user?.emailAddresses?.[0]?.verification?.status ===
                        'verified'
                          ? 'Verified'
                          : 'Unverified'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Email Status
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Bell className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <Badge variant="outline">Active</Badge>
                    <p className="text-sm text-muted-foreground">
                      Account Status
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No user data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator className="my-8" />

      {/* Subscription Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>
            Manage your subscription and billing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Current Plan</h4>
                <p className="text-sm text-muted-foreground capitalize">
                  {subscription.plan} Plan
                </p>
              </div>
              <Badge
                variant={
                  subscription.isPremium
                    ? 'default'
                    : subscription.isPro
                      ? 'secondary'
                      : 'outline'
                }
              >
                {subscription.plan}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Max Assets:</span>
                <span className="ml-2 font-medium">
                  {subscription.maxAssets}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Storage:</span>
                <span className="ml-2 font-medium">
                  {subscription.maxStorage}GB
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Bank Accounts:</span>
                <span className="ml-2 font-medium">
                  {subscription.maxBanks}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Viewers:</span>
                <span className="ml-2 font-medium">
                  {subscription.maxViewers}
                </span>
              </div>
            </div>

            {subscription.isFree && (
              <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
                <h4 className="font-medium text-amber-800 mb-2">
                  Upgrade to unlock more features
                </h4>
                <p className="text-sm text-amber-700 mb-3">
                  Get access to more assets, advanced analytics, and premium
                  features.
                </p>
                <Button
                  onClick={handleManageSubscription}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Manage Subscription
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Separator className="my-8" />

      {/* MCP Configuration Guide */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Assistant Integration (MCP)
              </CardTitle>
              <CardDescription>
                Connect your BCTech data to ChatGPT, Claude Desktop, or OpenAI MCP
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowMcpGuide(!showMcpGuide)}
            >
              {showMcpGuide ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Hide Guide
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show Guide
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {showMcpGuide && (
          <CardContent className="space-y-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                How It Works
              </h4>
              <p className="text-sm text-blue-800">
                BCTech exposes MCP-compatible HTTP endpoints that allow AI assistants to access your asset data.
                Choose your authentication method based on your MCP client: API tokens for HTTP access, or OAuth credentials for Claude Desktop and OpenAI MCP.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Step 1: Choose Authentication Method</h4>
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm font-medium mb-1">For HTTP Access / Custom Integrations:</p>
                    <p className="text-sm text-muted-foreground">
                      Create an API token in the API Tokens section below. Copy it immediately - you won't see it again!
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg bg-blue-50">
                    <p className="text-sm font-medium mb-1">For Claude Desktop & OpenAI MCP:</p>
                    <p className="text-sm text-muted-foreground">
                      Create an OAuth client in the OAuth Clients section below. Both Claude Desktop and OpenAI MCP require OAuth 2.0 client credentials (client_id and client_secret) for authentication.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Step 2: Use the Endpoints</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  The backend exposes MCP-compatible endpoints at:
                </p>
                <div className="bg-muted p-3 rounded-lg font-mono text-sm">
                  <code className="text-primary">https://api.bettercapital.us/api/mcp</code>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Step 3: Example Usage</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium mb-1">Get All Assets:</p>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs overflow-x-auto">
                      <div className="whitespace-pre-wrap break-words">
{`curl https://api.bettercapital.us/api/mcp/resources/assets \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      onClick={() => {
                        const code = `curl https://api.bettercapital.us/api/mcp/resources/assets \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`;
                        navigator.clipboard.writeText(code);
                        toast.success('Code copied to clipboard!');
                      }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-1">Get Portfolio Summary:</p>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs overflow-x-auto">
                      <div className="whitespace-pre-wrap break-words">
{`curl https://api.bettercapital.us/api/mcp/resources/portfolio/summary \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      onClick={() => {
                        const code = `curl https://api.bettercapital.us/api/mcp/resources/portfolio/summary \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`;
                        navigator.clipboard.writeText(code);
                        toast.success('Code copied to clipboard!');
                      }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-1">Analyze Portfolio:</p>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs overflow-x-auto">
                      <div className="whitespace-pre-wrap break-words">
{`curl -X POST https://api.bettercapital.us/api/mcp/tools/analyze_portfolio \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"arguments": {}}'`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      onClick={() => {
                        const code = `curl -X POST https://api.bettercapital.us/api/mcp/tools/analyze_portfolio \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"arguments": {}}'`;
                        navigator.clipboard.writeText(code);
                        toast.success('Code copied to clipboard!');
                      }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">
                  💡 MCP Server Configuration
                </h4>
                <p className="text-sm text-blue-800 mb-3">
                  BCTech supports OAuth 2.0 for secure MCP server authentication. Configure your MCP client with:
                </p>
                <div className="space-y-2 text-sm text-blue-800">
                  <div>
                    <strong>MCP Server URL:</strong> <code className="bg-blue-100 px-1 rounded">https://api.bettercapital.us/api/mcp</code>
                  </div>
                  <div>
                    <strong>OAuth Discovery Endpoint:</strong> <code className="bg-blue-100 px-1 rounded">https://api.bettercapital.us/.well-known/oauth-authorization-server</code>
                  </div>
                  <div>
                    <strong>Token Endpoint:</strong> <code className="bg-blue-100 px-1 rounded">https://api.bettercapital.us/token</code>
                  </div>
                  <div>
                    <strong>Authorization Endpoint:</strong> <code className="bg-blue-100 px-1 rounded">https://api.bettercapital.us/authorize</code>
                  </div>
                </div>
                <p className="text-sm text-blue-800 mt-3">
                  Use the OAuth client credentials (client_id and client_secret) from the OAuth Clients section below. The discovery endpoint automatically provides all OAuth configuration details.
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2">Available Resources</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li><code className="bg-muted px-1 rounded">assets</code> - All your assets</li>
                  <li><code className="bg-muted px-1 rounded">portfolio/summary</code> - Portfolio KPIs and summary</li>
                  <li><code className="bg-muted px-1 rounded">entities</code> - Legal entities</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">Available Tools</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li><code className="bg-muted px-1 rounded">get_asset</code> - Get asset details</li>
                  <li><code className="bg-muted px-1 rounded">get_asset_transactions</code> - Get transactions</li>
                  <li><code className="bg-muted px-1 rounded">analyze_portfolio</code> - Portfolio analysis</li>
                  <li><code className="bg-muted px-1 rounded">get_kpis</code> - Get KPIs</li>
                  <li><code className="bg-muted px-1 rounded">search_assets</code> - Search assets</li>
                </ul>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-medium mb-2">Quick Test</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Test your API token with this command (replace YOUR_API_TOKEN):
                </p>
                <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                  <div className="whitespace-pre-wrap break-words">
{`curl https://api.bettercapital.us/api/mcp/resources \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    const testCmd = `curl https://api.bettercapital.us/api/mcp/resources -H "Authorization: Bearer YOUR_API_TOKEN"`;
                    navigator.clipboard.writeText(testCmd);
                    toast.success('Test command copied!');
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy Test Command
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      <Separator className="my-8" />

      {/* API Tokens Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Tokens
              </CardTitle>
              <CardDescription>
                Manage API tokens for MCP servers and integrations
              </CardDescription>
            </div>
            <Button onClick={() => setShowTokenDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Token
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tokensLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : tokens && tokens.length > 0 ? (
            <div className="space-y-4">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className="p-4 border rounded-lg space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{token.name}</h4>
                        {token.isExpired && (
                          <Badge variant="destructive">Expired</Badge>
                        )}
                        {!token.isActive && (
                          <Badge variant="secondary">Revoked</Badge>
                        )}
                        {token.isActive && !token.isExpired && (
                          <Badge variant="default">Active</Badge>
                        )}
                      </div>
                      {token.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {token.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span>
                          Created:{' '}
                          {new Date(token.createdAt).toLocaleDateString()}
                        </span>
                        {token.lastUsedAt && (
                          <span>
                            Last used:{' '}
                            {new Date(token.lastUsedAt).toLocaleDateString()}
                          </span>
                        )}
                        {token.expiresAt && (
                          <span>
                            Expires:{' '}
                            {new Date(token.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {token.isActive && !token.isExpired && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const configText = `BCTech MCP Configuration (HTTP Access):

Base URL: https://api.bettercapital.us/api/mcp
API Token: (Use your API token from above)

Example Usage:
curl https://api.bettercapital.us/api/mcp/resources/assets -H "Authorization: Bearer YOUR_API_TOKEN"

Available Resources:
- GET /api/mcp/resources/assets
- GET /api/mcp/resources/portfolio/summary
- GET /api/mcp/resources/entities

Available Tools:
- POST /api/mcp/tools/get_asset
- POST /api/mcp/tools/get_asset_transactions
- POST /api/mcp/tools/analyze_portfolio
- POST /api/mcp/tools/get_kpis
- POST /api/mcp/tools/search_assets

Note: Replace YOUR_API_TOKEN with your actual token. For Claude Desktop and OpenAI MCP, use OAuth clients instead.`;
                              navigator.clipboard.writeText(configText);
                              toast.success('Configuration copied to clipboard!');
                            }}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Config
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevokeToken(token.id)}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteToken(token.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                No API tokens created yet
              </p>
              <Button onClick={() => setShowTokenDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Token
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Token Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create API Token</DialogTitle>
            <DialogDescription>
              Create a new API token for MCP servers or other integrations.
              Store it securely - you won't be able to see it again.
            </DialogDescription>
          </DialogHeader>
          {newToken ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  ⚠️ Important: Copy this token now
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  This is the only time you'll be able to see this token. Store
                  it securely.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={newToken}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    onClick={() => handleCopyToken(newToken)}
                    variant="outline"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setNewToken(null);
                  setShowTokenDialog(false);
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="token-name">Name *</Label>
                <Input
                  id="token-name"
                  placeholder="e.g., MCP Server Token"
                  value={tokenForm.name}
                  onChange={(e) =>
                    setTokenForm({ ...tokenForm, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="token-description">Description (optional)</Label>
                <Textarea
                  id="token-description"
                  placeholder="Describe what this token will be used for"
                  value={tokenForm.description || ''}
                  onChange={(e) =>
                    setTokenForm({ ...tokenForm, description: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="token-expires">
                  Expires in (days, optional)
                </Label>
                <Input
                  id="token-expires"
                  type="number"
                  min="1"
                  max="365"
                  placeholder="Leave empty for no expiration"
                  value={tokenForm.expiresInDays || ''}
                  onChange={(e) =>
                    setTokenForm({
                      ...tokenForm,
                      expiresInDays: e.target.value
                        ? Number.parseInt(e.target.value)
                        : null,
                    })
                  }
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTokenDialog(false);
                    setTokenForm({ name: '', description: '', expiresInDays: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateToken}
                  disabled={createTokenMutation.isPending || !tokenForm.name.trim()}
                >
                  {createTokenMutation.isPending ? 'Creating...' : 'Create Token'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Separator className="my-8" />

      {/* OAuth Clients Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                OAuth Clients (for Claude Desktop & OpenAI MCP)
              </CardTitle>
              <CardDescription>
                Create OAuth 2.0 clients for Claude Desktop and OpenAI MCP integration
              </CardDescription>
            </div>
            <Button onClick={() => setShowOAuthDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create OAuth Client
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {oauthClientsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : oauthClients && oauthClients.length > 0 ? (
            <div className="space-y-4">
              {oauthClients.map((client) => (
                <div
                  key={client.id}
                  className="p-4 border rounded-lg space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{client.name}</h4>
                        {client.expiresAt && new Date(client.expiresAt) < new Date() && (
                          <Badge variant="destructive">Expired</Badge>
                        )}
                        {!client.isActive && (
                          <Badge variant="secondary">Revoked</Badge>
                        )}
                        {client.isActive && (!client.expiresAt || new Date(client.expiresAt) >= new Date()) && (
                          <Badge variant="default">Active</Badge>
                        )}
                      </div>
                      {client.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {client.description}
                        </p>
                      )}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Client ID:</span>
                          <code className="bg-muted px-2 py-1 rounded text-xs font-mono">
                            {client.clientId}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2"
                            onClick={() => {
                              navigator.clipboard.writeText(client.clientId);
                              toast.success('Client ID copied!');
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
                        <span>
                          Created:{' '}
                          {new Date(client.createdAt).toLocaleDateString()}
                        </span>
                        {client.lastUsedAt && (
                          <span>
                            Last used:{' '}
                            {new Date(client.lastUsedAt).toLocaleDateString()}
                          </span>
                        )}
                        {client.expiresAt && (
                          <span>
                            Expires:{' '}
                            {new Date(client.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {client.isActive && (!client.expiresAt || new Date(client.expiresAt) >= new Date()) && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const configText = `MCP Server Configuration (Claude Desktop & OpenAI MCP):

MCP Server URL: https://api.bettercapital.us/api/mcp
OAuth Discovery Endpoint: https://api.bettercapital.us/.well-known/oauth-authorization-server
OAuth Client ID: ${client.clientId}
OAuth Client Secret: (Use the secret shown when you created this client)

Token Endpoint: https://api.bettercapital.us/token
Authorization Endpoint: https://api.bettercapital.us/authorize

For Claude Desktop:
1. Open Claude Desktop settings
2. Add MCP server with URL: https://api.bettercapital.us/api/mcp
3. Configure OAuth with the client_id and client_secret above
4. The discovery endpoint will provide all OAuth configuration automatically

For OpenAI MCP:
1. Configure your MCP client with the server URL above
2. Use OAuth 2.0 client credentials flow
3. Provide client_id and client_secret for authentication
4. The discovery endpoint provides all required OAuth metadata

Note: The client secret is only shown once when you create the client. If you've lost it, you'll need to create a new OAuth client.`;
                              navigator.clipboard.writeText(configText);
                              toast.success('Configuration copied to clipboard!');
                            }}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Config
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevokeOAuthClient(client.id)}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteOAuthClient(client.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                No OAuth clients created yet
              </p>
              <Button onClick={() => setShowOAuthDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create OAuth Client
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* OAuth Client Creation Dialog */}
      <Dialog open={showOAuthDialog} onOpenChange={setShowOAuthDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create OAuth Client</DialogTitle>
            <DialogDescription>
              Create an OAuth 2.0 client for Claude Desktop or OpenAI MCP integration. You'll receive a client ID and client secret.
            </DialogDescription>
          </DialogHeader>
          {newOAuthClient ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-2">
                  ⚠️ Important: Save Your Credentials
                </h4>
                <p className="text-sm text-amber-800 mb-4">
                  The client secret is only shown once. Make sure to copy it now!
                </p>
                <div className="space-y-3">
                  <div>
                    <Label>Client ID</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-muted p-2 rounded font-mono text-sm">
                        {newOAuthClient.clientId}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(newOAuthClient.clientId);
                          toast.success('Client ID copied!');
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Client Secret</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-muted p-2 rounded font-mono text-sm">
                        {newOAuthClient.clientSecret}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(newOAuthClient.clientSecret);
                          toast.success('Client secret copied!');
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setNewOAuthClient(null);
                  setShowOAuthDialog(false);
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="oauth-name">Name *</Label>
                <Input
                  id="oauth-name"
                  placeholder="e.g., Claude Desktop, OpenAI MCP"
                  value={oauthForm.name}
                  onChange={(e) =>
                    setOauthForm({ ...oauthForm, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oauth-description">Description (optional)</Label>
                <Textarea
                  id="oauth-description"
                  placeholder="Describe what this OAuth client will be used for"
                  value={oauthForm.description || ''}
                  onChange={(e) =>
                    setOauthForm({ ...oauthForm, description: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oauth-expires">
                  Expires in (days, optional)
                </Label>
                <Input
                  id="oauth-expires"
                  type="number"
                  min="1"
                  max="365"
                  placeholder="Leave empty for no expiration"
                  value={oauthForm.expiresInDays || ''}
                  onChange={(e) =>
                    setOauthForm({
                      ...oauthForm,
                      expiresInDays: e.target.value
                        ? Number.parseInt(e.target.value)
                        : null,
                    })
                  }
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowOAuthDialog(false);
                    setOauthForm({ name: '', description: '', expiresInDays: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateOAuthClient}
                  disabled={createOAuthClientMutation.isPending || !oauthForm.name.trim()}
                >
                  {createOAuthClientMutation.isPending ? 'Creating...' : 'Create OAuth Client'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Separator className="my-8" />

      {/* Sign Out Section */}
      <Card className="border-destructive/20">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
            <h4 className="font-medium mb-2 text-destructive">Sign Out</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Sign out of your account. You will need to sign in again to access
              your data.
            </p>
            <SignOutButton>
              <Button variant="destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </SignOutButton>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
